import type { Logger } from "@expert/logging";
import type { GaiaPayloadConfigs } from "@expert/shared-types";
import type { JSX } from "react";

import { useReactAnalytics } from "@asurion-core/eventualize-react";
import { trackAppcuesEvent } from "@expert/monitoring";
import { Group } from "@mantine/core";

import type { QuickAction } from "./types";

import { useSendExpertAssistMessage } from "../../hooks";
import { useAutoscroll } from "../../utils";
import { QuickActionButton } from "./QuickActionButton";

export interface QuickActionGroupProps {
    actions: QuickAction[];
    setIsQuickActionsVisible: React.Dispatch<React.SetStateAction<boolean>>;
    logger: Logger;
    gaiaPayloadProps: GaiaPayloadConfigs;
    scrollRef?: React.RefObject<HTMLDivElement | null>;
}

export function QuickActionGroup({
    actions,
    setIsQuickActionsVisible,
    logger,
    gaiaPayloadProps,
    scrollRef,
}: QuickActionGroupProps): JSX.Element {
    const sendMessage = useSendExpertAssistMessage(logger, gaiaPayloadProps);
    const { dispatcher } = useReactAnalytics();
    const { start: startAutoscroll } = useAutoscroll(scrollRef);
    const onQuickActionClick = ({
        value,
        analytics: { actionId, eventData, extraContext },
        onClick,
        isVisibleAfterClick,
        prompt,
        source,
    }: QuickAction) => {
        void dispatcher.dispatchBusinessEvent(`Click_${actionId}`, {
            ...eventData,
            ...extraContext,
            button: value,
        });
        trackAppcuesEvent(`Click_${actionId}`, { actionId, actionType: "QuickAction" });

        if (!isVisibleAfterClick) {
            setIsQuickActionsVisible(false);
        }

        if (onClick) {
            onClick(value);
        } else {
            void sendMessage({ text: prompt ?? value, type: "QuickAction", source: source ?? "QuickAction" });
            startAutoscroll();
        }
    };

    const children = actions.map((action) => (
        <QuickActionButton
            key={`${action.analytics.actionId}-${action.value}`}
            name={action.analytics.actionId}
            value={action.value}
            loading={action.loading}
            disabled={action.disabled}
            onClick={() => onQuickActionClick(action)}
        />
    ));

    return (
        <Group gap="sm" mt="xs">
            {children}
        </Group>
    );
}
